<script setup>
  import { ref, watchEffect, onMounted, onBeforeUnmount } from 'vue';
  import { useUserStore } from '@/stores/UserStore';
  import BaseModal from '@/components/base/BaseModal.vue';
  import Icon from '@/components/icons/Icon.vue';
  import PopupMenuFavoriteItemActions from '@/components/PopupMenuFavoriteItemActions.vue';
  import ModalRenameProfile from '@/components/ModalRenameProfile.vue';
  import ModalDeleteProfile from '@/components/ModalDeleteProfile.vue';
  import ModalCopyProfile from '@/components/ModalCopyProfile.vue';
  import ModalCreateProfile from '@/components/ModalCreateProfile.vue';
  import draggable from 'vuedraggable';
  import RadioControl from '@/components/RadioControl.vue';

  const currentProfile = defineModel('currentProfileId', { required: true });
  const emits = defineEmits(['close']);

  const props = defineProps({
    userProfiles: {
      type: Array,
      required: true
    },
    show: {
      type: Boolean,
      default: false
    }
  });

  const userStore = useUserStore();

  const mode = ref('select-profile');
  const profiles = ref(props.userProfiles);
  const editProfileId = ref(null);
  const showModal = ref(false);
  const showRenameModal = ref(false);
  const showDeleteModal = ref(false);
  const showCopyModal = ref(false);
  const showCreateProfileModal = ref(false);

  const actionModalMap = {
    rename: showRenameModal,
    delete: showDeleteModal,
    copy: showCopyModal
  };

  function toggleMode() {
    mode.value = mode.value === 'select-profile' ? 'manage-profiles' : 'select-profile';
  }

  function handleProfileManagementActionClick(profileId, actionType) {
    editProfileId.value = profileId;
    const modalRef = actionModalMap[actionType];
    if (modalRef) modalRef.value = true;
  }

  async function handleSortEnd() {
    const newProfileOrder = profiles.value.map(profile => profile.profile_id);
    await userStore.sortProfiles(newProfileOrder);
    await userStore.getProfiles(true);
  }

  function closeModal() {
    console.log('closeModal')
    showModal.value = false;
    showRenameModal.value = false;
    showDeleteModal.value = false;
    showCopyModal.value = false;
    emits('close');
  }

  onMounted(() => {
    watchEffect(() => showModal.value = props.show);
  });
  
  watchEffect(() => profiles.value = props.userProfiles);
</script>

<template>
  <BaseModal
    v-model="showModal"
    :allowOutsideClick="true"
    height="tall"
    @close="closeModal"
  >
    <header class="jwx-modal__header">
      <h2 class="jwx-modal__title">User Profiles</h2>
    </header>
    <div class="jwx-modal__body">
      <div class="jwx-modal__section">
        <p class="jwx-modal__text">Select a user profile below to switch to it.</p>
        <p class="jwx-modal__text">To sort, rename, copy, or delete a profile, click the Manage button below.</p>
      </div>
      <div class="jwx-modal__section">
        <div class="jwx-modal__section-actions">
          <button
            type="button"
            class="jwx-button jwx-button--pill jwx-button--small"
            @click.prevent="showCreateProfileModal = true"
          >
            Create Profile
          </button>
          <button
            type="button"
            class="jwx-button jwx-button--pill jwx-button--small jwx-m-left-auto"
            :class="{ 'jwx-button--active': mode === 'manage-profiles' }"
            @click.prevent="toggleMode"
          >
            {{ mode === 'manage-profiles' ? "Done" : "Manage" }}
          </button>
        </div>
        <draggable
          :list="profiles"
          :item-key="(item) => item.profile_id"
          :disabled="mode === 'manage'"
          :animation="200"
          class="jwx-block jwx-table-view"
          tag="ul"
          draggable=".jwx-table-view__row"
          data-surface="1"
          @end="handleSortEnd"
        >
          <template #item="{ element }">
            <li class="jwx-table-view__row" :key="element.profile_id">
              <div v-if="mode === 'manage-profiles'" class="jwx-table-view__row-content">
                <div class="jwx-table-view__start-block jwx-table-view__drag-handle">
                  <Icon name="grip-dots" class="jwx-settings__drag-icon" />
                </div>
                <p class="jwx-table-view__row-label">{{ element.name }}</p>
                <div class="jwx-table-view__row-actions">
                  <PopupMenuFavoriteItemActions
                    :itemId="element.profile_id"
                    :hideActions="['move']"
                    @rename="handleProfileManagementActionClick(element.profile_id, 'rename')"
                    @copy="handleProfileManagementActionClick(element.profile_id, 'copy')"
                    @delete="handleProfileManagementActionClick(element.profile_id, 'delete')"
                  />
                </div>
              </div>

              <div v-else class="jwx-table-view__row-content">
                <label :for="`user-profile-${element.profile_id}`" class="jwx-table-view__row-label">
                  {{ element.name }}
                </label>
                <div class="jwx-table-view__row-actions">
                  <RadioControl
                    v-model="currentProfile"
                    :name="`user-profile-${element.profile_id}`"
                    :id="`user-profile-${element.profile_id}`"
                    :value="element.profile_id"
                    :srLabel="`Select ${element.name} as current profile`"
                  />
                </div>
              </div>
            </li>
          </template>
        </draggable>
      </div>
    </div>
    <div class="jwx-modal__footer">
      <div class="jwx-modal__actions">
        <button
          type="button"
          class="jwx-button jwx-button--success"
          @click.prevent="closeModal"
        >
          Close
        </button>
      </div>
    </div>
  </BaseModal>

  <ModalCreateProfile
    v-if="showCreateProfileModal"
    :show="showCreateProfileModal"
    @close="showCreateProfileModal = false"
  />
  <ModalRenameProfile
    v-if="showRenameModal"
    :show="showRenameModal"
    :profileId="editProfileId"
    @close="showRenameModal = false"
  />
  <ModalDeleteProfile
    v-if="showDeleteModal"
    :show="showDeleteModal"
    :profileId="editProfileId"
    @close="showDeleteModal = false"
  />
  <ModalCopyProfile
    v-if="showCopyModal"
    :show="showCopyModal"
    :profileId="editProfileId"
    @close="showCopyModal = false"
  />
</template>
